import { useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { ArticleContent } from '@/components/ui/ArticleContent'
import {
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import {
  useStrategicPillar,
  usePublishedStrategicPillars,
  useIncrementStrategicPillarView
} from '@/lib/api/hooks/strategic-pillar'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'


export default function StrategicPillar() {
  const { pillarSlug } = useParams()
  const navigate = useNavigate()

  // Fetch strategic pillar data from API
  const { data: pillarData, isLoading } = useStrategicPillar(pillarSlug)
  const { data: otherPillarsData } = usePublishedStrategicPillars({ page: 1, size: 10 }, {})
  const incrementViewMutation = useIncrementStrategicPillarView()

  // Extract data from API
  const apiPillar = pillarData?.data
  const allPillars = otherPillarsData?.data?.data || []

  // Increment view count when pillar is loaded
  useEffect(() => {
    if (pillarSlug && !incrementViewMutation.isPending) {
      incrementViewMutation.mutate(pillarSlug)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillarSlug])

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  // Strategic pillar data from API
  const pillar = apiPillar ? {
    id: pillarSlug || '',
    title: apiPillar.title,
    heroImage: apiPillar.coverImage,
    date: apiPillar.datePublished ? formatDate(apiPillar.datePublished) : formatDate(apiPillar.createdAt),
    altText: apiPillar.coverImageTitle || apiPillar.title,
    content: apiPillar.content || '',
    articleTag: apiPillar.articleTag || []
  } : null

  // Find current pillar index in the list for navigation
  const currentPillarIndex = allPillars.findIndex(p => p.strategicPillarId === pillarSlug)
  const hasPrevious = currentPillarIndex > 0
  const hasNext = currentPillarIndex >= 0 && currentPillarIndex < allPillars.length - 1
  const previousPillar = hasPrevious ? allPillars[currentPillarIndex - 1] : null
  const nextPillar = hasNext ? allPillars[currentPillarIndex + 1] : null

  // Get other pillars for sidebar (excluding current pillar)
  const otherPillars = allPillars
    .filter(p => p.strategicPillarId !== pillarSlug)
    .slice(0, 4)
    .map((p) => ({
      id: p.strategicPillarId,
      title: p.title,
      image: p.coverImage
    }))

  const handleShare = (platform: string) => {
    if (!pillar) return

    const url = window.location.href
    const title = pillar.title

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank')
        break
      case 'copy':
        navigator.clipboard.writeText(url)
        break
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Strategic Pillars', href: '/programmes' },
    { label: pillar?.title || 'Loading...', href: '', active: true }
  ]

  // Show loading state
  if (isLoading) {
    return <LoadingSpinner fullScreen size="lg" message="Loading strategic pillar..." />
  }

  // Show error state if pillar not found
  if (!pillar) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Strategic pillar not found</p>
          <Link to="/programmes" className="text-wine-100 hover:underline mt-4 inline-block">
            Back to Strategic Pillars
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="px-4 md:px-20">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-20 pb-16">
        <div className="max-w-[1268px] mx-auto">
          {/* Pillar Header */}
          <div className="flex flex-col gap-8 mb-16">
            <div className="flex flex-col gap-4">
              <h1 className="text-4xl md:text-6xl font-normal font-['PT_Serif'] leading-tight text-gray-900">
                {pillar.title}
              </h1>
              {pillar.date && (
                <p className="text-base font-medium text-gray-500">
                  {pillar.date}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <img
                src={pillar.heroImage}
                alt={pillar.altText}
                className="w-full h-[300px] md:h-[502px] object-cover"
              />
            </div>
          </div>

          {/* Content and Sidebar */}
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Pillar Content */}
            <div className="flex-1 lg:w-[804px]">
              {/* Pillar Content */}
              <div className="flex flex-col gap-24 mb-24">
                <div className="flex flex-col gap-12">
                  <div className="flex flex-col gap-10">
                    <ArticleContent content={pillar.content} className="text-lg leading-relaxed text-gray-900" />

                    {/* Article Tags */}
                    {pillar.articleTag && pillar.articleTag.length > 0 && (
                      <div className="flex flex-wrap gap-4">
                        {pillar.articleTag.map((tag, index) => (
                          <span
                            key={index}
                            className="px-6 py-1.5 bg-imageBg-100 rounded-full text-base font-medium text-forestGreen-100"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Social Share */}
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-normal text-gray-500 font-['PT_Serif']">SHARE</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Facebook className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Twitter className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <Linkedin className="w-5 h-5 text-gray-800" />
                      </button>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-6 h-6 hover:opacity-70"
                      >
                        <LinkIcon className="w-5 h-5 text-gray-800" />
                      </button>
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  {(hasPrevious || hasNext) && (
                    <div className="flex justify-between items-center">
                      {hasPrevious && previousPillar && (
                        <button
                          onClick={() => navigate(`/programmes/strategic-pillars/${previousPillar.strategicPillarId}`)}
                          className="px-3 py-2 bg-white rounded-lg border border-gray-300 flex items-center gap-2 hover:bg-gray-50"
                        >
                          <ChevronLeft className="w-4 h-4 text-gray-900" />
                          <span className="text-base font-medium text-gray-900">Previous</span>
                        </button>
                      )}
                      {hasNext && nextPillar && (
                        <button
                          onClick={() => navigate(`/programmes/strategic-pillars/${nextPillar.strategicPillarId}`)}
                          className="px-3 py-2 bg-white rounded-lg border border-gray-300 flex items-center gap-2 hover:bg-gray-50 ml-auto"
                        >
                          <span className="text-base font-medium text-gray-900">Next</span>
                          <ChevronRight className="w-4 h-4 text-gray-900" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-96 flex flex-col gap-6">
              {/* Other Strategic Pillars */}
              {otherPillars.length > 0 && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-2xl font-normal font-['PT_Serif'] text-gray-900">OTHER STRATEGIC PILLARS</h2>
                  <div className="flex flex-col">
                    {otherPillars.map((otherPillar) => (
                      <Link
                        key={otherPillar.id}
                        to={`/programmes/strategic-pillars/${otherPillar.id}`}
                        className="p-2 hover:bg-gray-50"
                      >
                        <div className="flex gap-4">
                          <img
                            src={otherPillar.image}
                            alt={otherPillar.title}
                            className="w-24 h-24 object-cover"
                              onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                    }}
                          />
                          <div className="flex-1">
                            <h3 className="text-lg leading-relaxed text-gray-900 font-['PT_Serif']">
                              {otherPillar.title}
                            </h3>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Articles */}
              <RecentArticles />

              {/* Newsletter Signup */}
              <Newsletter />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
